import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Lock } from "lucide-react";
import { NAV_LINKS } from "@/lib/siteData";
import Logo from "@/components/Logo";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-ivory/95 backdrop-blur-md border-b border-brandgray shadow-[0_2px_20px_rgba(41,72,144,0.06)]" : "bg-ivory/70 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="brand-logo" className="flex items-center group" aria-label="Home">
          <Logo variant="dark" className="h-6 md:h-8" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.to.replace(/\//g, "") || "home"}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${
                  isActive ? "text-navy font-semibold" : "text-black/70 hover:text-navy"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <Link
            to="/dealer-login"
            data-testid="nav-dealer-login"
            className="inline-flex items-center gap-1.5 text-sm tracking-wide text-black/70 hover:text-navy transition-colors"
          >
            <Lock size={13} />
            <span>Dealer Login</span>
          </Link>
          <Link
            to="/contact"
            data-testid="nav-cta-contact"
            className="inline-flex items-center gap-2 bg-coral hover:bg-[#d66a54] text-white text-sm px-5 py-2.5 tracking-wide transition-colors"
          >
            Get in Touch
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          className="lg:hidden p-2 text-navy"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-ivory border-t border-brandgray">
          <div className="px-5 py-6 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-link-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `py-3 px-2 border-b border-brandgray/60 text-base ${
                    isActive ? "text-navy font-semibold" : "text-black/80"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/dealer-login"
              data-testid="mobile-nav-dealer-login"
              className="mt-4 inline-flex items-center justify-center gap-2 border border-navy/20 text-navy py-3 tracking-wide"
            >
              <Lock size={14} />
              <span>Dealer Login</span>
            </Link>
            <Link
              to="/contact"
              data-testid="mobile-nav-cta"
              className="mt-2 inline-flex justify-center bg-coral text-white py-3 tracking-wide"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
